// src/pages/playlists/SaveToPlaylistSheet.jsx
// Handoff §1. Ticking saves straight away; Done / Esc / scrim closes and the
// caller shows "Saved to …".
import { useState } from "react";
import { PlsLayer, PlsHead } from "./PlaylistLayer";
import { PlsIc } from "./playlistIcons";
import { useMyPlaylists, toggleItem, createPlaylist } from "./playlistsStore";
import { LIMITS } from "./playlistsModel";
import { errorMessage } from "./playlistsApi";

export default function SaveToPlaylistSheet({ itemKey, title, kind = "audio", onClose }) {
  const { status, playlists } = useMyPlaylists();
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const atLimit = playlists.length >= LIMITS.playlists;
  // No playlists yet → open with the create field showing.
  const showCreate = creating || (status === "ready" && playlists.length === 0);

  const toggle = (p) => {
    setError("");
    toggleItem(p.id, itemKey).catch((err) => setError(errorMessage(err, "Couldn't update that playlist.")));
  };

  const create = async (e) => {
    e.preventDefault();
    const n = name.trim();
    if (!n || busy) return;
    setBusy(true);
    setError("");
    try {
      await createPlaylist({ name: n, items: [itemKey] });
      setName("");
      setCreating(false);
    } catch (err) {
      setError(errorMessage(err, "Couldn't create the playlist."));
    } finally {
      setBusy(false);
    }
  };

  const done = () => {
    const ins = playlists.filter((p) => p.items.includes(itemKey));
    if (!ins.length) return onClose(null);
    onClose(ins.length === 1 ? <>Saved to <b>{ins[0].name}</b></> : <>Saved to <b>{ins.length} playlists</b></>);
  };

  return (
    <PlsLayer label="Save to playlist" onClose={done}>
      <PlsHead title="Save to playlist" onClose={done} />
      <div className="pls-item">{kind === "video" ? PlsIc.video(14) : PlsIc.audio(14)}<span>{title}</span></div>
      <div className="pls-body">
        {status === "loading" && <div className="pls-none">Loading your playlists…</div>}
        {status === "error" && <div className="pls-none">Couldn't load your playlists.</div>}
        {playlists.map((p) => {
          const on = p.items.includes(itemKey);
          const full = !on && p.items.length >= LIMITS.items;
          return (
            <button key={p.id} type="button" className={`pls-opt ${on ? "is-on" : ""}`} onClick={() => toggle(p)}
              aria-pressed={on} disabled={full} title={full ? `Full (${LIMITS.items} lectures)` : undefined}>
              <span className="pls-check">{on && PlsIc.check(13)}</span>
              <span className="nm">{p.name}</span>
              <span className="ct">{full ? "Full" : p.items.length}</span>
            </button>
          );
        })}
        {status === "ready" &&
          (showCreate && !atLimit ? (
            <form className="pls-newform" onSubmit={create}>
              <input autoFocus className="pls-input" value={name} onChange={(e) => setName(e.target.value)}
                placeholder="Playlist name" maxLength={LIMITS.name} />
              <button type="submit" className="pls-btn" disabled={!name.trim() || busy}>Create</button>
            </form>
          ) : (
            <button type="button" className="pls-opt pls-new" onClick={() => setCreating(true)} disabled={atLimit}
              title={atLimit ? `You can have up to ${LIMITS.playlists} playlists.` : undefined}>
              <span className="pls-plus">{PlsIc.plus(13)}</span>
              <span className="nm">{atLimit ? `${LIMITS.playlists} playlists is the limit` : "New playlist"}</span>
            </button>
          ))}
      </div>
      {error && <div className="pls-err" role="alert">{error}</div>}
      <div className="pls-foot"><button type="button" className="pls-btn" onClick={done}>Done</button></div>
    </PlsLayer>
  );
}
